import { CornerDownRight, Plus, X } from "lucide-react-native";
import { FC } from "react";

import {
  Button,
  ButtonIcon,
  ButtonText,
  DropdownPicker,
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question, Rule } from "@/types/resources";
import { RuleOperator } from "@/types/utils";
import {
  defaultOperatorForQuestionType,
  operatorsForQuestionType,
  useOperatorLabels,
} from "./operators";

interface RulesEditorProps {
  allQuestions: Question[];
  currentQuestionOrder: number;
  disabled?: boolean;
  hasOptionSpecificRules?: boolean;
  onRulesChange: (rules: Rule[]) => void;
  previousConditionQuestions: Question[];
  rules: Rule[];
}

/**
 * Returns true when the rules editor has anything meaningful to render for
 * the given question position. Skip rules require at least one question after
 * the current one to point to, so anything else is unreachable.
 *
 * Returns a type predicate so callers can use the result to narrow
 * `currentQuestionOrder` from `number | undefined` to `number`.
 */
export const hasApplicableRules = (
  allQuestions: Question[],
  currentQuestionOrder: number | undefined
): currentQuestionOrder is number => {
  if (currentQuestionOrder === undefined) return false;
  return allQuestions.some((q) => q.order > currentQuestionOrder);
};

const RulesEditor: FC<RulesEditorProps> = ({
  allQuestions,
  currentQuestionOrder,
  disabled = false,
  hasOptionSpecificRules = false,
  onRulesChange,
  previousConditionQuestions,
  rules,
}) => {
  const { i18n } = useLocalization();
  const operatorLabels = useOperatorLabels();

  // Split rules into types
  const conditionalRules = rules.filter(
    (r) => r.source_question_id !== null && r.option_index !== null
  );
  const defaultRules = rules.filter(
    (r) => r.option_index === null && r.source_question_id === null
  );
  const probabilityBasedRules = defaultRules.filter(
    (r) => r.probability !== null
  );
  const fallbackDefaultRule = defaultRules.find((r) => r.probability === null);

  // Validate probability sum
  const probabilitySum =
    probabilityBasedRules.reduce(
      (sum, rule) => sum + (rule.probability || 0),
      0
    ) * 100;
  const isProbabilitySumValid = probabilitySum <= 100;

  // Only show questions after the current one
  const availableQuestions = allQuestions
    .filter((q) => q.order > currentQuestionOrder)
    .sort((a, b) => a.order - b.order);

  if (availableQuestions.length === 0) {
    return null;
  }

  const getFallbackDropdownValue = () => {
    if (!fallbackDefaultRule) return "";
    if (fallbackDefaultRule.target_question_id === null)
      return "__END_SURVEY__";
    return fallbackDefaultRule.target_question_id;
  };

  const getSourceQuestionOptions = (sourceQuestionId: string) => {
    const sourceQ = previousConditionQuestions.find(
      (q) => q.id === sourceQuestionId
    );
    return sourceQ?.options || [];
  };

  const dropdownItems = [
    { label: i18n.t("questions.continueToNext"), value: "" },
    { label: i18n.t("questions.endSurvey"), value: "__END_SURVEY__" },
    ...availableQuestions.map((q) => ({
      label: `${q.order}. ${q.text || i18n.t("questions.untitled")}`,
      value: q.id,
    })),
  ];

  const targetDropdownItems = [
    { label: i18n.t("questions.endSurvey"), value: "__END_SURVEY__" },
    ...availableQuestions.map((q) => ({
      label: `${q.order}. ${q.text || i18n.t("questions.untitled")}`,
      value: q.id,
    })),
  ];

  const getSourceQuestionItems = (excludeConditionalRuleIndex: number = -1) =>
    previousConditionQuestions.map((q) => ({
      isDisabled:
        getAvailableOptionsForQuestion(q.id, excludeConditionalRuleIndex)
          .length === 0,
      label: `${q.order}. ${q.text || i18n.t("questions.untitled")}`,
      value: q.id,
    }));

  // Sort rules in the correct order
  const sortRules = (rulesToSort: Rule[]): Rule[] => {
    const conditional = rulesToSort.filter(
      (r) => r.source_question_id !== null && r.option_index !== null
    );
    const probabilityBased = rulesToSort.filter(
      (r) =>
        r.option_index === null &&
        r.source_question_id === null &&
        r.probability !== null
    );
    const fallback = rulesToSort.filter(
      (r) =>
        r.option_index === null &&
        r.source_question_id === null &&
        r.probability === null
    );

    // Sort conditional rules by source question order, then option index
    conditional.sort((a, b) => {
      const aSourceOrder =
        previousConditionQuestions.find((q) => q.id === a.source_question_id)
          ?.order ?? 0;
      const bSourceOrder =
        previousConditionQuestions.find((q) => q.id === b.source_question_id)
          ?.order ?? 0;

      if (aSourceOrder !== bSourceOrder) {
        return aSourceOrder - bSourceOrder;
      }

      return (a.option_index ?? 0) - (b.option_index ?? 0);
    });

    // Sort probability-based rules by target question order (null targets last)
    probabilityBased.sort((a, b) => {
      // Null targets (end survey) should come last
      if (a.target_question_id === null && b.target_question_id === null)
        return 0;
      if (a.target_question_id === null) return 1;
      if (b.target_question_id === null) return -1;

      const aTargetOrder =
        allQuestions.find((q) => q.id === a.target_question_id)?.order ?? 0;
      const bTargetOrder =
        allQuestions.find((q) => q.id === b.target_question_id)?.order ?? 0;

      return aTargetOrder - bTargetOrder;
    });

    return [...conditional, ...probabilityBased, ...fallback];
  };

  // Handlers
  const handleAddConditionalRule = () => {
    const firstQuestion = getFirstQuestionWithAvailableOptions();
    if (!firstQuestion) return;

    const availableOptions = getAvailableOptionsForQuestion(firstQuestion.id);
    if (availableOptions.length === 0) return;

    const newRule: Rule = {
      created_at: new Date().toISOString(),
      id: "",
      operator: defaultOperatorForQuestionType(firstQuestion.type),
      option_index: availableOptions[0],
      probability: null,
      source_question_id: firstQuestion.id,
      target_question_id: null,
      updated_at: new Date().toISOString(),
    };
    onRulesChange(sortRules([...rules, newRule]));
  };

  const handleAddDefaultRule = () => {
    const availableTargets = getAvailableTargetsForProbabilityRules();
    if (availableTargets.length === 0) return;

    const firstTarget = availableTargets[0];
    const targetQuestionId =
      firstTarget.value === "__END_SURVEY__" ? null : firstTarget.value;

    const newRule: Rule = {
      created_at: new Date().toISOString(),
      id: "",
      operator: RuleOperator.Equal,
      option_index: null,
      probability: 0.2,
      source_question_id: null,
      target_question_id: targetQuestionId,
      updated_at: new Date().toISOString(),
    };
    onRulesChange(sortRules([...rules, newRule]));
  };

  const handleUpdateRule = (ruleIndex: number, updates: Partial<Rule>) => {
    const newRules = [...rules];
    newRules[ruleIndex] = {
      ...newRules[ruleIndex],
      ...updates,
      updated_at: new Date().toISOString(),
    };
    onRulesChange(sortRules(newRules));
  };

  const handleRemoveRule = (ruleIndex: number) => {
    onRulesChange(sortRules(rules.filter((_, idx) => idx !== ruleIndex)));
  };

  const handleFallbackDefaultRuleChange = (value: string) => {
    const otherRules = rules.filter(
      (r) =>
        !(
          r.option_index === null &&
          r.source_question_id === null &&
          r.probability === null
        )
    );

    if (value === "") {
      onRulesChange(sortRules(otherRules));
    } else {
      const newRule: Rule = {
        created_at: fallbackDefaultRule?.created_at || new Date().toISOString(),
        id: fallbackDefaultRule?.id || "",
        operator: RuleOperator.Equal,
        option_index: null,
        probability: null,
        source_question_id: null,
        target_question_id: value === "__END_SURVEY__" ? null : value,
        updated_at: new Date().toISOString(),
      };
      onRulesChange(sortRules([...otherRules, newRule]));
    }
  };

  // Track used source question + option combinations
  const getUsedOptionsForSource = (
    sourceQuestionId: string,
    excludeRuleIndex: number
  ): Set<number> => {
    const used = new Set<number>();
    conditionalRules.forEach((rule, index) => {
      if (
        index !== excludeRuleIndex &&
        rule.source_question_id === sourceQuestionId &&
        rule.option_index !== null
      ) {
        used.add(rule.option_index);
      }
    });
    return used;
  };

  // Get available options for a question (excluding current rule being edited)
  const getAvailableOptionsForQuestion = (
    questionId: string,
    excludeConditionalRuleIndex: number = -1
  ): number[] => {
    const question = previousConditionQuestions.find(
      (q) => q.id === questionId
    );
    if (!question) return [];

    const usedOptions = getUsedOptionsForSource(
      questionId,
      excludeConditionalRuleIndex
    );
    const availableOptions: number[] = [];

    for (let i = 0; i < (question.options?.length || 0); i++) {
      if (!usedOptions.has(i)) {
        availableOptions.push(i);
      }
    }

    return availableOptions;
  };

  // Get first question with available options
  const getFirstQuestionWithAvailableOptions = (): Question | null => {
    for (const question of previousConditionQuestions) {
      const availableOptions = getAvailableOptionsForQuestion(question.id);
      if (availableOptions.length > 0) {
        return question;
      }
    }
    return null;
  };

  // Check if any questions have available options
  const hasQuestionsWithAvailableOptions =
    getFirstQuestionWithAvailableOptions() !== null;

  // Get used target questions in probability-based rules
  const getUsedTargetsInProbabilityRules = (
    excludeProbabilityRuleIndex: number = -1
  ): Set<string | null> => {
    const used = new Set<string | null>();
    probabilityBasedRules.forEach((rule, index) => {
      if (index !== excludeProbabilityRuleIndex) {
        used.add(rule.target_question_id);
      }
    });
    return used;
  };

  // Get available target questions for probability rules
  const getAvailableTargetsForProbabilityRules = (
    excludeProbabilityRuleIndex: number = -1
  ): { label: string; value: string }[] => {
    const usedTargets = getUsedTargetsInProbabilityRules(
      excludeProbabilityRuleIndex
    );

    const availableTargets: { label: string; value: string }[] = [];

    // Add "End Survey" if not used
    if (!usedTargets.has(null)) {
      availableTargets.push({
        label: i18n.t("questions.endSurvey"),
        value: "__END_SURVEY__",
      });
    }

    // Add available questions
    availableQuestions.forEach((q) => {
      if (!usedTargets.has(q.id)) {
        availableTargets.push({
          label: `${q.order}. ${q.text || i18n.t("questions.untitled")}`,
          value: q.id,
        });
      }
    });

    return availableTargets;
  };

  // Check if any targets are available for probability rules
  const hasAvailableTargetsForProbabilityRules =
    getAvailableTargetsForProbabilityRules().length > 0;

  // Get target dropdown items for probability rules with disabled state
  const getTargetItemsForProbabilityRule = (
    excludeProbabilityRuleIndex: number = -1
  ) => {
    const usedTargets = getUsedTargetsInProbabilityRules(
      excludeProbabilityRuleIndex
    );

    return [
      {
        isDisabled: usedTargets.has(null),
        label: i18n.t("questions.endSurvey"),
        value: "__END_SURVEY__",
      },
      ...availableQuestions.map((q) => ({
        isDisabled: usedTargets.has(q.id),
        label: `${q.order}. ${q.text || i18n.t("questions.untitled")}`,
        value: q.id,
      })),
    ];
  };

  return (
    <VStack className="gap-3 pt-2">
      {/* Header */}
      <HStack className="items-center gap-3">
        <CornerDownRight className="text-primary-600" size={18} />
        <Text className="text-sm text-typography-700">
          {i18n.t("questions.afterThisQuestion")}
        </Text>
      </HStack>

      {/* Content indented */}
      <VStack className="gap-3 pl-8">
        {/* Conditional rules */}
        {conditionalRules.map((rule, index) => {
          // Find the actual index in the full rules array
          const ruleIndex = rules.indexOf(rule);

          const sourceOptions = getSourceQuestionOptions(
            rule.source_question_id!
          );
          const usedOptions = getUsedOptionsForSource(
            rule.source_question_id!,
            index
          );
          const optionItems = sourceOptions.map((option, optIndex) => ({
            isDisabled: usedOptions.has(optIndex),
            label:
              option || i18n.t("questions.option", { number: optIndex + 1 }),
            value: optIndex.toString(),
          }));
          const sourceQuestionItems = getSourceQuestionItems(index);
          const sourceQuestion = previousConditionQuestions.find(
            (q) => q.id === rule.source_question_id
          );
          const operatorItems = (
            sourceQuestion
              ? operatorsForQuestionType(sourceQuestion.type)
              : [RuleOperator.Equal, RuleOperator.NotEqual]
          ).map((op) => ({
            label: operatorLabels[op],
            value: op,
          }));
          const rulesCount = conditionalRules.length;
          const sourceItemsKey = sourceQuestionItems
            .map((i) => `${i.value}:${i.label}:${i.isDisabled}`)
            .join(",");
          const optionItemsKey = optionItems
            .map((i) => `${i.value}:${i.label}:${i.isDisabled}`)
            .join(",");
          const targetItemsKey = targetDropdownItems
            .map((i) => `${i.value}:${i.label}`)
            .join(",");

          return (
            <HStack key={rule.id || index} className="items-center gap-2">
              <Text className="text-sm text-typography-600">
                {i18n.t("questions.ifAnswer")}
              </Text>
              <DropdownPicker
                className="flex-1"
                isDisabled={disabled}
                items={sourceQuestionItems}
                onChange={(value) => {
                  const nextSource = previousConditionQuestions.find(
                    (q) => q.id === value
                  );
                  const availableOptions = getAvailableOptionsForQuestion(
                    value,
                    index
                  );
                  handleUpdateRule(ruleIndex, {
                    operator: nextSource
                      ? defaultOperatorForQuestionType(nextSource.type)
                      : RuleOperator.Equal,
                    option_index:
                      availableOptions.length > 0 ? availableOptions[0] : 0,
                    source_question_id: value,
                  });
                }}
                placeholder={i18n.t("questions.selectOption")}
                selectKey={`source-${index}-${rulesCount}-${sourceItemsKey}`}
                value={rule.source_question_id || ""}
              />
              <DropdownPicker
                className="flex-1"
                isDisabled={disabled}
                items={operatorItems}
                onChange={(value) =>
                  handleUpdateRule(ruleIndex, {
                    operator: value as RuleOperator,
                  })
                }
                placeholder={i18n.t("questions.selectOption")}
                selectKey={`operator-${index}-${rulesCount}-${rule.source_question_id}`}
                value={rule.operator}
              />
              <DropdownPicker
                className="flex-1"
                isDisabled={disabled}
                items={optionItems}
                onChange={(value) =>
                  handleUpdateRule(ruleIndex, {
                    option_index: parseInt(value, 10),
                  })
                }
                placeholder={i18n.t("questions.selectOption")}
                selectKey={`option-${index}-${rulesCount}-${rule.source_question_id}-${optionItemsKey}`}
                value={rule.option_index?.toString() || "0"}
              />
              <Text className="text-sm text-typography-600">→</Text>
              <DropdownPicker
                className="flex-1"
                isDisabled={disabled}
                items={targetDropdownItems}
                onChange={(value) =>
                  handleUpdateRule(ruleIndex, {
                    target_question_id:
                      value === "__END_SURVEY__" ? null : value,
                  })
                }
                placeholder={i18n.t("questions.selectOption")}
                selectKey={`target-${index}-${rulesCount}-${targetItemsKey}`}
                value={rule.target_question_id || "__END_SURVEY__"}
              />
              <Pressable
                className="p-1"
                disabled={disabled}
                onPress={() => handleRemoveRule(ruleIndex)}
              >
                <X className="text-typography-400" size={16} />
              </Pressable>
            </HStack>
          );
        })}

        {/* Probability-based default rules */}
        {probabilityBasedRules.map((rule, index) => {
          // Find the actual index in the full rules array
          const ruleIndex = rules.indexOf(rule);

          const targetItems = getTargetItemsForProbabilityRule(index);
          const rulesCount = probabilityBasedRules.length;
          const targetItemsKey = targetItems
            .map((i) => `${i.value}:${i.label}:${i.isDisabled}`)
            .join(",");

          return (
            <HStack key={rule.id || index} className="items-center gap-2">
              <Input
                className="w-20 border border-outline-200 rounded-xl bg-background-50"
                isDisabled={disabled}
              >
                <InputField
                  keyboardType="numeric"
                  onChangeText={(value) => {
                    const num = parseFloat(value);
                    if (!isNaN(num) && num >= 0 && num <= 100) {
                      handleUpdateRule(ruleIndex, { probability: num / 100 });
                    }
                  }}
                  placeholder="20"
                  value={
                    rule.probability !== null
                      ? Math.round(rule.probability * 100).toString()
                      : ""
                  }
                />
              </Input>
              <Text className="text-sm text-typography-600">%</Text>
              <Text className="text-sm text-typography-600">
                {i18n.t("questions.arrow")}
              </Text>
              <DropdownPicker
                className="flex-1"
                isDisabled={disabled}
                items={targetItems}
                onChange={(value) => {
                  const availableTargets =
                    getAvailableTargetsForProbabilityRules(index);
                  // If the selected value is already used by another rule, pick first available
                  const usedTargets = getUsedTargetsInProbabilityRules(index);
                  const selectedTarget =
                    value === "__END_SURVEY__" ? null : value;

                  let targetToUse = selectedTarget;
                  if (usedTargets.has(selectedTarget)) {
                    // Pick first available target
                    if (availableTargets.length > 0) {
                      const firstAvailable = availableTargets[0];
                      targetToUse =
                        firstAvailable.value === "__END_SURVEY__"
                          ? null
                          : firstAvailable.value;
                    }
                  }

                  handleUpdateRule(ruleIndex, {
                    target_question_id: targetToUse,
                  });
                }}
                placeholder={i18n.t("questions.selectOption")}
                selectKey={`default-${index}-${rulesCount}-${targetItemsKey}`}
                value={rule.target_question_id || "__END_SURVEY__"}
              />
              <Pressable
                className="p-1"
                disabled={disabled}
                onPress={() => handleRemoveRule(ruleIndex)}
              >
                <X className="text-typography-400" size={16} />
              </Pressable>
            </HStack>
          );
        })}

        {/* Validation warning */}
        {probabilityBasedRules.length > 0 && !isProbabilitySumValid && (
          <Text className="text-sm text-error-600">
            {i18n.t("questions.probabilitySumError", {
              sum: i18n.numberToPercentage(probabilitySum, { precision: 0 }),
            })}
          </Text>
        )}

        {/* Fallback default rule (no probability) */}
        <HStack className="items-center gap-2">
          <Text className="text-sm text-typography-600">
            {i18n.t("questions.default")}:
          </Text>
          <DropdownPicker
            className="flex-1"
            isDisabled={disabled}
            items={dropdownItems}
            onChange={handleFallbackDefaultRuleChange}
            placeholder={i18n.t("questions.selectOption")}
            selectKey={`fallback-default-${dropdownItems.map((i) => `${i.value}:${i.label}`).join(",")}`}
            value={getFallbackDropdownValue()}
          />
        </HStack>

        {/* Add rule buttons */}
        <HStack className="gap-3">
          {hasQuestionsWithAvailableOptions && (
            <Button
              className="self-start"
              isDisabled={disabled}
              onPress={handleAddConditionalRule}
              variant="link"
            >
              <ButtonIcon as={Plus} size="sm" />
              <ButtonText>{i18n.t("questions.addConditionalRule")}</ButtonText>
            </Button>
          )}
          {hasAvailableTargetsForProbabilityRules && (
            <Button
              className="self-start"
              isDisabled={disabled}
              onPress={handleAddDefaultRule}
              variant="link"
            >
              <ButtonIcon as={Plus} size="sm" />
              <ButtonText>{i18n.t("questions.addProbabilityRule")}</ButtonText>
            </Button>
          )}
        </HStack>

        {/* Precedence message — a short reminder of how rules are ordered */}
        {rules.length > 0 && (
          <Text className="text-xs text-typography-400">
            {hasOptionSpecificRules
              ? i18n.t("questions.rulePrecedence")
              : i18n.t("questions.conditionalRulesTakePrecedence")}
          </Text>
        )}
      </VStack>
    </VStack>
  );
};

export default RulesEditor;
